import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, HardHat, ShieldCheck } from 'lucide-react';

import { useAuth } from '@/auth/auth-context';
import { BrandLockup } from '@/components/brand-logo';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/field';
import { SHOW_DEV_OTP } from '@/lib/env';
import { errorMessage } from '@/lib/errors';

/**
 * Phone number, then a code. No password anywhere.
 *
 * Two steps on one screen rather than two routes, so the back button means
 * "leave" rather than "lose the code I just typed". A rider signs in at the
 * start of a shift, outdoors, usually in a hurry.
 *
 * There is no sign-UP here, unlike the customer app. A rider account is
 * created by the PLATFORM in the admin panel — the role, then a `riders` row
 * with a vehicle on it — so verifying an unknown number lands on a session
 * with no rider profile behind it, and `NotARiderScreen` names which half is
 * missing.
 */
export function SignInScreen() {
  const { requestOtp, verifyOtp } = useAuth();

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const codeInput = useRef<HTMLInputElement>(null);

  // Focus follows the step. Without this the rider lands on a screen whose
  // only field is not focused, and types the code into nothing.
  useEffect(() => {
    if (step === 'code') codeInput.current?.focus();
  }, [step]);

  const digits = phone.replace(/\D/g, '');
  const phoneReady = digits.length >= 10;
  const codeReady = code.length >= 4;

  async function sendCode() {
    if (working || !phoneReady) return;
    setWorking(true);
    setError(null);
    try {
      setDevCode(await requestOtp(digits));
      setStep('code');
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setWorking(false);
    }
  }

  async function signIn() {
    if (working || !codeReady) return;
    setWorking(true);
    setError(null);
    try {
      await verifyOtp({ phone: digits, code });
      // Nothing to navigate: the route tree swaps the moment the session
      // exists, and pushing a route here would race that swap.
    } catch (verifyError) {
      setError(errorMessage(verifyError));
      setCode('');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <main
        id="main"
        className="mx-auto flex w-full max-w-app flex-1 flex-col justify-center px-gutter py-10"
      >
        <div className="grid gap-2">
          <BrandLockup size={34} className="mb-4" />
          <h1 className="display text-display-xs text-ink">
            {step === 'phone' ? 'Sign in to ride' : 'Check your messages'}
          </h1>
          <p className="text-[0.9375rem] leading-normal text-ink-muted">
            {step === 'phone'
              ? 'Your number is your account. We will text you a code.'
              : `We sent a code to ${digits}.`}
          </p>
        </div>

        <form
          className="mt-8 grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void (step === 'phone' ? sendCode() : signIn());
          }}
        >
          {step === 'phone' ? (
            <Field
              label="Mobile number"
              error={error}
              hint="The number the office registered you on."
            >
              {(ids) => (
                <Input
                  {...ids}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  autoFocus
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    setError(null);
                  }}
                  data-numeric
                />
              )}
            </Field>
          ) : (
            <Field label="Six-digit code" error={error}>
              {(ids) => (
                <Input
                  {...ids}
                  ref={codeInput}
                  /*
                   * `inputMode` rather than `type="number"`: a number input
                   * strips leading zeros, and plenty of codes start with one.
                   * `one-time-code` is what lets iOS and Android offer the SMS
                   * straight from the keyboard.
                   */
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  // No `maxLength`, for the reason given in handover-sheet.tsx:
                  // the browser clamps before React sees the value, so a paste
                  // containing anything non-numeric loses real digits. The
                  // handler below strips and then slices, which cannot.
                  placeholder="- - - - - -"
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                    setError(null);
                  }}
                  data-numeric
                  className="text-center text-2xl font-bold tracking-[0.4em]"
                />
              )}
            </Field>
          )}

          <Button
            type="submit"
            size="xl"
            block
            disabled={step === 'phone' ? !phoneReady : !codeReady}
            loading={working}
            loadingLabel={step === 'phone' ? 'Sending' : 'Signing in'}
          >
            {step === 'phone' ? 'Send me a code' : 'Sign in'}
          </Button>

          {step === 'code' ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  setDevCode(null);
                  setError(null);
                }}
              >
                <ArrowLeft aria-hidden="true" />
                Change number
              </Button>
              <Button variant="link" size="sm" onClick={() => void sendCode()}>
                Send it again
              </Button>
            </div>
          ) : null}
        </form>

        {/*
         * The development stand-in for SMS.
         *
         * The backend returns `devCode` outside production so the platform can
         * be worked on without an SMS contract. It is labelled as what it is,
         * and compiled out of a release build — see SHOW_DEV_OTP.
         */}
        {SHOW_DEV_OTP && devCode ? (
          <Card variant="warning" className="mt-6">
            <CardBody className="flex items-start gap-3">
              <HardHat className="mt-0.5 size-5 shrink-0 text-warning-ink" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-semibold text-warning-ink">Development build</p>
                <p className="mt-0.5 text-sm leading-snug text-ink-muted">
                  No SMS provider is wired up yet. Your code is{' '}
                  <span data-numeric className="font-bold text-ink">
                    {devCode}
                  </span>
                  .
                </p>
              </div>
            </CardBody>
          </Card>
        ) : null}
      </main>

      {/* The page ends on purpose rather than trailing off into empty space. */}
      <footer className="mx-auto w-full max-w-app px-gutter pb-[max(env(safe-area-inset-bottom),var(--space-6))]">
        <p className="flex items-start gap-2 border-t border-line pt-5 text-sm leading-snug text-ink-subtle">
          <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            Only numbers the office has registered as riders can sign in here. Customers use the
            main site.
          </span>
        </p>
      </footer>
    </div>
  );
}
